class YuanB2bQuote extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.overlay = this.querySelector('[data-yuan-b2b-overlay]');
    this.dialog = this.querySelector('[data-yuan-b2b-dialog]');
    this.searchInput = this.querySelector('[data-yuan-b2b-search]');
    this.results = this.querySelector('[data-yuan-b2b-results]');
    this.optionsEl = this.querySelector('[data-yuan-b2b-options]');
    this.addQuoteBtn = this.querySelector('[data-yuan-b2b-add-quote]');
    this.toDetailsBtn = this.querySelector('[data-yuan-b2b-to-details]');
    this.whatsappBtn = this.querySelector('[data-yuan-b2b-whatsapp]');
    this.errorEl = this.querySelector('[data-yuan-b2b-error]');
    this.qtyInput = this.querySelector('[data-yuan-b2b-qty-input]');
    this.quoteBox = this.querySelector('[data-yuan-b2b-quote-box]');
    this.quoteList = this.querySelector('[data-yuan-b2b-quote-list]');
    this.quoteCount = this.querySelector('[data-yuan-b2b-quote-count]');

    this.step = 1;
    this.product = null;
    this.activeVariantId = null;
    this.catalog = [];
    this.catalogLoaded = false;
    this.catalogPromise = null;
    this.quoteLines = [];
    this.searchTimer = null;
    this.hasOpened = false;
    this.hasAutoOpened = false;
    this.lastOpenSource = null;
    this.lastFocused = null;
    this.sessionKey = 'yuan-b2b-quote-once';

    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    this.searchInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.renderQuoteBox();
    this.setupTriggers();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.delayTimer) window.clearTimeout(this.delayTimer);
  }

  setupTriggers() {
    if (this.dataset.autoOpen !== 'true') return;
    if (this.alreadyShownThisSession()) return;

    const mode = this.dataset.triggerMode || 'either';
    const delay = Number(this.dataset.openDelay) || 0;
    const scrollPercent = Number(this.dataset.scrollPercent) || 0;

    if (mode === 'delay' || mode === 'either') {
      this.delayTimer = window.setTimeout(() => this.maybeAutoOpen('delay'), delay);
    }

    if ((mode === 'scroll' || mode === 'either') && scrollPercent > 0) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }

  onScroll() {
    if (this.hasOpened || this.alreadyShownThisSession()) return;
    const mode = this.dataset.triggerMode || 'either';
    if (mode !== 'scroll' && mode !== 'either') return;

    const target = Number(this.dataset.scrollPercent) || 0;
    if (target <= 0) return;

    const doc = document.documentElement;
    const maxScroll = Math.max(doc.scrollHeight - window.innerHeight, 1);
    const percent = (window.scrollY / maxScroll) * 100;
    if (percent >= target) this.maybeAutoOpen('scroll');
  }

  oncePerSessionEnabled() {
    const value = String(this.dataset.oncePerSession || '').toLowerCase();
    return value === 'true' || value === '1';
  }

  alreadyShownThisSession() {
    if (!this.oncePerSessionEnabled()) return false;
    if (window.__yuanB2bQuoteShown) return true;
    try {
      if (sessionStorage.getItem(this.sessionKey) === '1') return true;
    } catch (error) {
      /* ignore */
    }
    try {
      return document.cookie.split(';').some((part) => part.trim() === `${this.sessionKey}=1`);
    } catch (error) {
      return false;
    }
  }

  markShownThisSession() {
    if (!this.oncePerSessionEnabled()) return;
    window.__yuanB2bQuoteShown = true;
    try {
      sessionStorage.setItem(this.sessionKey, '1');
    } catch (error) {
      /* ignore */
    }
    try {
      document.cookie = `${this.sessionKey}=1; path=/; SameSite=Lax`;
    } catch (error) {
      /* ignore */
    }
  }

  maybeAutoOpen() {
    if (this.hasAutoOpened || this.alreadyShownThisSession()) return;
    // Never interrupt a popup the shopper opened themselves and still has open.
    if (this.isOpen()) return;
    this.open('auto');
  }

  isOpen() {
    return this.dialog ? !this.dialog.hidden : false;
  }

  /**
   * @param {'auto'|'manual'} source - Only 'auto' spends the once-per-session
   *   budget; a manual open from the header button always works and leaves the
   *   scheduled auto-open intact.
   */
  open(source = 'manual') {
    this.lastOpenSource = source;
    this.lastFocused = document.activeElement;
    this.ensureCatalogLoaded().catch(() => {});

    if (this.hasOpened) {
      this.overlay.hidden = false;
      this.dialog.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.searchInput?.focus();
      if (source === 'auto') {
        this.hasAutoOpened = true;
        if (this.oncePerSessionEnabled()) this.markShownThisSession();
      }
      return;
    }

    this.hasOpened = true;
    this.overlay.hidden = false;
    this.dialog.hidden = false;
    document.body.classList.add('overflow-hidden');
    this.goToStep(1);
    this.searchInput?.focus();

    if (source === 'auto') {
      this.hasAutoOpened = true;
      window.removeEventListener('scroll', this.onScroll);
      if (this.delayTimer) window.clearTimeout(this.delayTimer);
      if (this.oncePerSessionEnabled()) this.markShownThisSession();
    }
  }

  close() {
    this.overlay.hidden = true;
    this.dialog.hidden = true;
    document.body.classList.remove('overflow-hidden');
    if (this.lastOpenSource === 'auto') this.markShownThisSession();
    if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
  }

  onClick(event) {
    if (event.target.closest('[data-yuan-b2b-close], [data-yuan-b2b-overlay]')) {
      this.close();
      return;
    }

    const result = event.target.closest('[data-yuan-b2b-result]');
    if (result) {
      this.selectProduct(result.dataset.handle);
      return;
    }

    const removeBtn = event.target.closest('[data-yuan-b2b-remove-line]');
    if (removeBtn) {
      this.removeQuoteLine(removeBtn.dataset.variantId);
      return;
    }

    const qtyBtn = event.target.closest('[data-yuan-b2b-qty]');
    if (qtyBtn) {
      this.changeQuantity(qtyBtn);
      return;
    }

    if (event.target.closest('[data-yuan-b2b-add-quote]')) {
      this.addCurrentToQuote();
      return;
    }

    if (event.target.closest('[data-yuan-b2b-back]')) {
      if (this.step === 3) this.goToStep(1);
      else this.goToStep(Math.max(1, this.step - 1));
      return;
    }

    if (event.target.closest('[data-yuan-b2b-to-details]')) {
      if (this.quoteLines.length) this.goToStep(3);
      return;
    }

    if (event.target.closest('[data-yuan-b2b-whatsapp]')) {
      this.handleWhatsApp(event);
    }
  }

  onInput(event) {
    if (event.target.matches('[data-yuan-b2b-search]')) {
      this.scheduleSearch(event.target.value.trim());
      return;
    }

    if (event.target.matches('[data-yuan-b2b-qty-input]')) {
      this.normalizeQtyInput(event.target);
      this.updateNav();
      return;
    }

    if (event.target.matches('[data-yuan-b2b-name], [data-yuan-b2b-phone], [data-yuan-b2b-address]')) {
      this.updateWhatsAppState();
    }
  }

  onChange(event) {
    if (event.target.matches('[data-yuan-b2b-option]')) {
      this.syncActiveVariant();
    }
  }

  goToStep(step) {
    this.step = step;
    this.querySelectorAll('[data-yuan-b2b-step]').forEach((panel) => {
      const active = Number(panel.dataset.yuanB2bStep) === step;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    this.querySelectorAll('[data-step-dot]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.stepDot) <= step);
    });
    if (step === 1) this.renderQuoteBox();
    if (step === 3) {
      this.renderSummary();
      this.updateWhatsAppState();
    }
  }

  scheduleSearch(query) {
    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.filterCatalog(query), 160);
  }

  ensureCatalogLoaded() {
    if (this.catalogLoaded) return Promise.resolve(this.catalog);
    if (this.catalogPromise) return this.catalogPromise;

    this.catalogPromise = this.loadCatalog()
      .then(() => {
        this.catalogLoaded = true;
        return this.catalog;
      })
      .finally(() => {
        this.catalogPromise = null;
      });

    return this.catalogPromise;
  }

  async loadCatalog() {
    const base = (this.dataset.productsUrl || '/collections/all/products.json').split('?')[0];
    this.results.innerHTML = `<li class="yuan-b2b__result yuan-b2b__result--empty">${this.escape(this.dataset.loadingLabel || '…')}</li>`;

    try {
      const pages = [];
      for (let page = 1; page <= 4; page += 1) {
        const response = await fetch(`${base}?limit=250&page=${page}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) break;
        const data = await response.json();
        const batch = Array.isArray(data?.products) ? data.products : [];
        if (!batch.length) break;
        pages.push(...batch);
        if (batch.length < 250) break;
      }

      this.catalog = pages.map((product) => ({
        id: product.id,
        handle: product.handle,
        title: product.title,
        image: product.images?.[0]?.src || product.featured_image || '',
        price: product.variants?.[0]?.price,
      }));

      this.filterCatalog(this.searchInput?.value.trim() || '');
    } catch (error) {
      this.results.innerHTML = `<li class="yuan-b2b__result yuan-b2b__result--empty">${this.escape(this.dataset.noResults)}</li>`;
      throw error;
    }
  }

  filterCatalog(query) {
    const normalized = String(query || '')
      .trim()
      .toLocaleLowerCase('ar');
    const products = !normalized
      ? this.catalog
      : this.catalog.filter((product) => String(product.title || '').toLocaleLowerCase('ar').includes(normalized));
    this.renderResults(products);
  }

  renderResults(products) {
    if (!products.length) {
      this.results.innerHTML = `<li class="yuan-b2b__result yuan-b2b__result--empty">${this.escape(this.dataset.noResults)}</li>`;
      return;
    }

    this.results.innerHTML = products
      .slice(0, 80)
      .map((product) => {
        const image = product.image || '';
        const price =
          product.price != null && product.price !== '' ? this.formatMoney(Number(product.price)) : '';
        return `
          <li>
            <button type="button" class="yuan-b2b__result" role="option" data-yuan-b2b-result data-handle="${this.escape(product.handle)}">
              <span class="yuan-b2b__result-media">${image ? `<img src="${this.escape(image)}" alt="" width="48" height="48" loading="lazy">` : ''}</span>
              <span class="yuan-b2b__result-copy">
                <strong>${this.escape(product.title)}</strong>
                ${price ? `<small>${price}</small>` : ''}
              </span>
            </button>
          </li>
        `;
      })
      .join('');
  }

  async selectProduct(handle) {
    if (!handle) return;
    this.searchInput.value = '';
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) throw new Error('product');
      this.product = await response.json();
      this.renderProduct();
      this.goToStep(2);
    } catch (error) {
      this.results.innerHTML = `<li class="yuan-b2b__result yuan-b2b__result--empty">${this.escape(this.dataset.noResults)}</li>`;
    }
  }

  renderProduct() {
    const product = this.product;
    if (!product) return;

    this.querySelector('[data-yuan-b2b-product-title]').textContent = product.title;
    this.querySelector('[data-yuan-b2b-product-price]').textContent = this.formatMoney(product.price / 100);

    const media = this.querySelector('[data-yuan-b2b-media]');
    const image =
      product.featured_image || product.images?.[0] || product.variants?.[0]?.featured_image?.src || '';
    media.innerHTML = image
      ? `<img src="${this.escape(typeof image === 'string' ? image : image.src)}" alt="${this.escape(product.title)}" width="120" height="120" loading="lazy">`
      : '';

    this.optionsEl.innerHTML = '';
    const optionNames = (product.options || []).map((option) =>
      typeof option === 'string' ? option : option?.name || `Option ${option?.position || ''}`
    );
    optionNames.forEach((optionName, optionIndex) => {
      const values = [
        ...new Set(product.variants.map((variant) => variant[`option${optionIndex + 1}`]).filter(Boolean)),
      ];
      const label = document.createElement('label');
      label.className = 'yuan-b2b__field';
      label.innerHTML = `
        <span class="yuan-b2b__label">${this.escape(optionName)}</span>
        <span class="yuan-b2b__select-wrap">
          <select class="yuan-b2b__select" data-yuan-b2b-option data-option-index="${optionIndex}">
            ${values.map((value) => `<option value="${this.escape(value)}">${this.escape(value)}</option>`).join('')}
          </select>
        </span>
      `;
      this.optionsEl.appendChild(label);
    });

    const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];
    if (firstAvailable) {
      optionNames.forEach((_, index) => {
        const select = this.optionsEl.querySelector(`[data-option-index="${index}"]`);
        if (select) select.value = firstAvailable[`option${index + 1}`];
      });
    }

    this.qtyInput.value = '0';
    this.syncActiveVariant();
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-yuan-b2b-option]')].map((select) => select.value);
  }

  findVariant(options) {
    if (!this.product) return null;
    return (
      this.product.variants.find((variant) =>
        options.every((value, index) => variant[`option${index + 1}`] === value)
      ) || null
    );
  }

  syncActiveVariant() {
    const options = this.selectedOptions();
    const variant = this.findVariant(options) || this.product?.variants?.[0];
    if (!variant) return;

    this.activeVariantId = variant.id;
    const available = Boolean(variant.available);
    this.querySelector('[data-yuan-b2b-active]')?.classList.toggle('is-unavailable', !available);
    this.querySelector('[data-yuan-b2b-active-title]').textContent =
      variant.title || variant.name || this.product.title;
    this.querySelector('[data-yuan-b2b-product-price]').textContent = this.formatMoney(variant.price / 100);

    const media = this.querySelector('[data-yuan-b2b-media]');
    const image = variant.featured_image?.src || this.product.featured_image || this.product.images?.[0];
    if (image && media) {
      media.innerHTML = `<img src="${this.escape(typeof image === 'string' ? image : image)}" alt="" width="120" height="120" loading="lazy">`;
    }

    this.qtyInput.disabled = !available;
    this.querySelectorAll('[data-yuan-b2b-qty]').forEach((button) => {
      button.disabled = !available;
    });
    if (!available) this.qtyInput.value = '0';

    this.refreshOptionAvailability(options);
    this.updateNav();
  }

  refreshOptionAvailability(currentOptions) {
    this.querySelectorAll('[data-yuan-b2b-option]').forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex);
      [...select.options].forEach((optionEl) => {
        const trial = currentOptions.slice();
        trial[optionIndex] = optionEl.value;
        optionEl.disabled = !this.findVariant(trial);
      });
    });
  }

  changeQuantity(button) {
    const input = this.qtyInput;
    if (!input || input.disabled) return;
    const current = Number(input.value) || 0;
    const step = Number(input.step) || 1;
    const next =
      button.dataset.yuanB2bQty === 'increase' ? current + step : Math.max(0, current - step);
    input.value = String(next);
    this.updateNav();
  }

  normalizeQtyInput(input) {
    input.value = String(Math.max(0, Math.floor(Number(input.value) || 0)));
  }

  currentLine() {
    if (!this.product || !this.activeVariantId) return null;
    const quantity = Number(this.qtyInput?.value) || 0;
    if (!quantity) return null;
    const variant = this.product.variants.find((item) => String(item.id) === String(this.activeVariantId));
    if (!variant || !variant.available) return null;
    return {
      id: variant.id,
      quantity,
      title: variant.title || variant.name || this.product.title,
      price: variant.price,
      options: [variant.option1, variant.option2, variant.option3].filter(Boolean),
      productTitle: this.product.title,
      productHandle: this.product.handle,
      image: variant.featured_image?.src || this.product.featured_image || '',
    };
  }

  addCurrentToQuote() {
    const line = this.currentLine();
    if (!line) return;

    const existing = this.quoteLines.find((item) => String(item.id) === String(line.id));
    if (existing) existing.quantity += line.quantity;
    else this.quoteLines.push(line);

    this.qtyInput.value = '0';
    this.updateNav();
    this.goToStep(1);
    this.renderQuoteBox();
    this.filterCatalog(this.searchInput?.value.trim() || '');
  }

  removeQuoteLine(variantId) {
    this.quoteLines = this.quoteLines.filter((line) => String(line.id) !== String(variantId));
    this.renderQuoteBox();
    if (this.step === 3) {
      if (!this.quoteLines.length) this.goToStep(1);
      else {
        this.renderSummary();
        this.updateWhatsAppState();
      }
    }
  }

  renderQuoteBox() {
    if (!this.quoteBox || !this.quoteList) return;
    const count = this.quoteLines.reduce((total, line) => total + line.quantity, 0);
    this.quoteBox.hidden = this.quoteLines.length === 0;
    if (this.quoteCount) this.quoteCount.textContent = `${this.quoteLines.length} منتج · ${count} كمية`;

    this.quoteList.innerHTML = this.quoteLines
      .map(
        (line) => `
        <li class="yuan-b2b__quote-item">
          <span class="yuan-b2b__quote-copy">
            <strong>${this.escape(line.productTitle)}</strong>
            <small>${this.escape(line.title)} × ${line.quantity}</small>
          </span>
          <button type="button" class="yuan-b2b__quote-remove" data-yuan-b2b-remove-line data-variant-id="${line.id}" aria-label="${this.escape(this.dataset.removeLabel)}">×</button>
        </li>
      `
      )
      .join('');

    if (this.toDetailsBtn) this.toDetailsBtn.disabled = this.quoteLines.length === 0;
  }

  updateNav() {
    if (this.addQuoteBtn) this.addQuoteBtn.disabled = !this.currentLine();
  }

  renderSummary() {
    const summary = this.querySelector('[data-yuan-b2b-summary]');
    if (!summary) return;
    if (!this.quoteLines.length) {
      summary.innerHTML = `<p>${this.escape(this.dataset.quoteEmpty)}</p>`;
      return;
    }
    summary.innerHTML = `
      <strong>${this.escape(this.dataset.summaryTitle || 'ملخص طلب الجملة')}</strong>
      <ul>
        ${this.quoteLines
          .map(
            (line) =>
              `<li>${this.escape(line.productTitle)} — ${this.escape(line.title)} × ${line.quantity} — ${this.formatMoney(line.price / 100)}</li>`
          )
          .join('')}
      </ul>
    `;
  }

  updateWhatsAppState() {
    const ready = this.canSendWhatsApp();
    this.whatsappBtn.classList.toggle('is-disabled', !ready);
    this.whatsappBtn.setAttribute('aria-disabled', String(!ready));
    if (ready) {
      this.whatsappBtn.href = this.buildWhatsAppUrl();
      this.errorEl.hidden = true;
    } else {
      this.whatsappBtn.href = '#';
    }
  }

  canSendWhatsApp() {
    const name = this.querySelector('[data-yuan-b2b-name]')?.value.trim();
    const phone = this.querySelector('[data-yuan-b2b-phone]')?.value.trim();
    const address = this.querySelector('[data-yuan-b2b-address]')?.value.trim();
    return Boolean(this.dataset.whatsapp && name && phone && address && this.quoteLines.length);
  }

  buildMessage() {
    const name = this.querySelector('[data-yuan-b2b-name]')?.value.trim();
    const phone = this.querySelector('[data-yuan-b2b-phone]')?.value.trim();
    const address = this.querySelector('[data-yuan-b2b-address]')?.value.trim();
    const items = this.quoteLines
      .map(
        (line) =>
          `• ${line.productTitle} | ${line.title} | الكمية: ${line.quantity} | ${this.formatMoney(line.price / 100)}`
      )
      .join('\n');

    return [
      'طلب جملة — يوان',
      '',
      `الاسم: ${name}`,
      `الموبايل: ${phone}`,
      `العنوان: ${address}`,
      '',
      'المنتجات:',
      items,
    ].join('\n');
  }

  buildWhatsAppUrl() {
    const number = this.dataset.whatsapp;
    return `https://wa.me/${number}?text=${encodeURIComponent(this.buildMessage())}`;
  }

  handleWhatsApp(event) {
    if (!this.canSendWhatsApp()) {
      event.preventDefault();
      this.errorEl.hidden = false;
      this.errorEl.textContent = this.dataset.waMissing || 'أكمل البيانات والمنتجات أولًا';
      this.updateWhatsAppState();
      return;
    }
    this.whatsappBtn.href = this.buildWhatsAppUrl();
  }

  formatMoney(amount) {
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'ar-EG', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(amount);
    return `${formatted} ${this.dataset.currencySuffix || 'ج.م'}`;
  }

  escape(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}

if (!customElements.get('yuan-b2b-quote')) {
  customElements.define('yuan-b2b-quote', YuanB2bQuote);
}

/*
 * External triggers (e.g. the header button) live outside the custom element,
 * so listen once on the document rather than binding per trigger. This also
 * covers triggers injected after load.
 */
if (!window.__yuanB2bQuoteTriggerBound) {
  window.__yuanB2bQuoteTriggerBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-yuan-b2b-open]');
    if (!trigger) return;

    const popup = document.querySelector('yuan-b2b-quote');
    if (!popup) return;

    event.preventDefault();

    customElements.whenDefined('yuan-b2b-quote').then(() => {
      if (typeof popup.open === 'function') popup.open('manual');
    });
  });
}
